import React, { useState } from 'react';

import { useMutation } from '@apollo/client';
import { useFetch } from '@xingternal/hops';

import { Headline } from '@xingternal/typography';
import { FormattedMessage, useIntl } from 'react-intl';

import MaltImageUpload from '@xingternal/malt-image-upload';
import type { ShowErrorProps } from '@xingternal/pages-page-error';

import { uploadFile } from '../../utils';
import CropImage from './CropImage';
import { dataURLtoBlob } from './utils';

import UPLOAD_REQUEST_MUTATION from './UploadRequestMutation.graphql';

import styles from './styles.css';

export type ImageUploadCopy = {
  headline?: string;
  discardButton?: string;
  publishButton?: string;
  hint: string;
  label: string;
  onDragLabel: string;
};

type UploadProps = {
  maxSize?: number;
  noHeadline?: boolean;
  descriptionCopyKey?: string;
  onCropReady?: () => void;
  onDragCrop?: () => void;
  onChange?: () => void;
  onCancel?: () => void;
  onPublish: (
    croppedImageData: string,
    base64CroppedImage: any,
    originalImageData: string,
    imageUploaded: any
  ) => void;
  onUploadStart?: () => void;
  onDiscard: () => void;
  previewImage?: string;
  shouldUploadOriginal?: boolean;
  showError: ShowErrorProps;
  imageUploadCopy?: ImageUploadCopy;
  aspectRatio?: number;
  children?: React.ReactNode;
  cropperLeftButton?: React.ReactNode;
  showUploading?: boolean;
};

type HandleCroppedImage = {
  blob: Blob;
  croppedImageUrl: string;
};

type UploadImageParams = {
  blob?: Blob;
  name: string;
};

const Upload = ({
  maxSize = 5000000,
  noHeadline,
  descriptionCopyKey,
  onCropReady,
  onDragCrop,
  onDiscard,
  onChange,
  previewImage,
  shouldUploadOriginal,
  aspectRatio,
  showError,
  onPublish,
  imageUploadCopy,
  children,
  cropperLeftButton = null,
  showUploading = false,
  onUploadStart,
}: UploadProps) => {
  const [resetCompontKey, setResetComponentKey] = useState(0);
  const [imageUploaded, setImageUploaded] = useState(previewImage || null);
  const [cropState, setCropState] = useState<boolean>(false);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fetch = useFetch();
  const [uploadRequest] = useMutation(UPLOAD_REQUEST_MUTATION);
  const intl = useIntl();

  const handleCroppedImage = ({
    blob,
    croppedImageUrl,
  }: HandleCroppedImage) => {
    setIsUploading(true);
    onUploadStart && onUploadStart();

    setCropState(false);

    uploadImage(
      {
        blob: dataURLtoBlob(imageUploaded),
        name: `image`,
      },
      {
        blob,
        name: `image`,
      },
      croppedImageUrl
    );
  };

  const uploadImage = async (
    originalImage: UploadImageParams,
    croppedImage: UploadImageParams,
    base64CroppedImage: string
  ) => {
    const croppedImageData = await uploadFile(
      croppedImage,
      uploadRequest,
      fetch
    );

    const originalImageData = shouldUploadOriginal
      ? await uploadFile(originalImage, uploadRequest, fetch)
      : null;

    if (
      !croppedImageData?.id ||
      (shouldUploadOriginal && !originalImageData?.id)
    ) {
      showError({
        message: 'EP_GENERAL_FEEDBACK_ERROR',
        error: croppedImageData?.error,
      });
    } else {
      await onPublish(
        croppedImageData.id,
        base64CroppedImage,
        originalImageData?.id,
        imageUploaded
      );
    }
    setIsUploading(false);
  };

  const handleUpload = (image: any) => {
    const imageBlob = dataURLtoBlob(image);
    if (imageBlob?.size > maxSize) {
      showError({
        message: 'EP_IMAGE_UPLOAD_SIZE_FORMAT_ERROR',
      });
      setResetComponentKey(resetCompontKey + 1);
    } else {
      setImageUploaded(image);
      if (onChange) onChange();
    }
  };

  return (
    <>
      {!imageUploaded ? (
        <>
          {!noHeadline && (
            <Headline size={'xxlarge'} className={styles.uploadHeadline}>
              <FormattedMessage
                id={imageUploadCopy?.headline}
                defaultMessage={imageUploadCopy?.headline}
              />
            </Headline>
          )}
          <MaltImageUpload
            key={resetCompontKey}
            accept="image/jpeg, image/png, image/bmp"
            hint={intl.formatMessage({
              id: imageUploadCopy?.hint,
            })}
            label={intl.formatMessage({
              id: imageUploadCopy?.label,
            })}
            onDragLabel={intl.formatMessage({
              id: imageUploadCopy?.onDragLabel,
            })}
            className={styles.imageUploader}
            onChange={(image: any) => handleUpload(image)}
            onError={() =>
              showError({
                message: 'EP_IMAGE_UPLOAD_SIZE_FORMAT_ERROR',
              })
            }
          />
        </>
      ) : (
        <CropImage
          noHeadline={noHeadline}
          descriptionCopyKey={descriptionCopyKey}
          onCropReady={onCropReady}
          onDragCrop={onDragCrop}
          onDiscard={onDiscard}
          showError={showError}
          isUploading={isUploading || showUploading}
          image={imageUploaded}
          onChange={handleCroppedImage}
          setCropState={setCropState}
          cropState={cropState}
          aspectRatio={aspectRatio}
          discardCopy={imageUploadCopy?.discardButton}
          publishCopy={imageUploadCopy?.publishButton}
          children={children}
          leftButton={cropperLeftButton}
        />
      )}
    </>
  );
};

export default Upload;
